import { useMemo } from 'react';

import type { AhoyPropertyOption, SalesforceFieldMapping } from '../types';
import {
  EditableMappingRow,
  MAPPING_ROW_GRID,
  type MappingUpdate,
} from './EditableMappingRow';

export type { AhoyPropertyOption };

interface MappingSectionsProps {
  mappings: SalesforceFieldMapping[];
  availableAhoyProperties: AhoyPropertyOption[];
  isMutating: boolean;
  onUpdate: (mappingId: number, body: MappingUpdate) => void;
}

const COLUMN_HEADER_LABEL_CLASS =
  'text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]';
const SECTION_HEADING_CLASS =
  'text-sm font-semibold text-[var(--color-text-primary)]';

function isSynced(m: SalesforceFieldMapping): boolean {
  return !!m.ahoy_property;
}

function MappingTable({
  mappings,
  availableAhoyProperties,
  isMutating,
  onUpdate,
}: Pick<
  MappingSectionsProps,
  'mappings' | 'availableAhoyProperties' | 'isMutating' | 'onUpdate'
>) {
  return (
    <div className='overflow-hidden rounded-xl border border-[var(--color-border-secondary)]'>
      <div
        className={`hidden bg-[var(--color-bg-secondary)] px-4 py-2.5 sm:block ${MAPPING_ROW_GRID}`}
      >
        <span className={COLUMN_HEADER_LABEL_CLASS}>Salesforce field</span>
        <span className={COLUMN_HEADER_LABEL_CLASS}>Ahoy property</span>
        <span className={COLUMN_HEADER_LABEL_CLASS}>Direction</span>
      </div>
      <div className='divide-y divide-[var(--color-border-secondary)]'>
        {mappings.map(mapping => (
          <EditableMappingRow
            key={mapping.id}
            mapping={mapping}
            availableAhoyProperties={availableAhoyProperties}
            isSaving={isMutating}
            onUpdate={onUpdate}
          />
        ))}
      </div>
    </div>
  );
}

export function MappingSections({
  mappings,
  availableAhoyProperties,
  isMutating,
  onUpdate,
}: MappingSectionsProps) {
  const synced = useMemo(() => mappings.filter(isSynced), [mappings]);
  const unmapped = useMemo(
    () => mappings.filter(m => !isSynced(m)),
    [mappings]
  );

  return (
    <div className='space-y-8'>
      {synced.length > 0 && (
        <section className='space-y-2'>
          <h3 className={SECTION_HEADING_CLASS}>Synced fields</h3>
          <MappingTable
            mappings={synced}
            availableAhoyProperties={availableAhoyProperties}
            isMutating={isMutating}
            onUpdate={onUpdate}
          />
        </section>
      )}

      {unmapped.length > 0 && (
        <section className='space-y-2'>
          <h3 className={SECTION_HEADING_CLASS}>Unmapped Salesforce fields</h3>
          <MappingTable
            mappings={unmapped}
            availableAhoyProperties={availableAhoyProperties}
            isMutating={isMutating}
            onUpdate={onUpdate}
          />
        </section>
      )}
    </div>
  );
}
